// Conversation context extraction and analysis utilities
export const extractConversationContext = (messages, coachingType = 'business', vertical = 'business') => {
  // For opsiq vertical, ignore coachingType - it's purely business/employee-focused
  if (vertical === 'opsiq') {
    coachingType = null;
  }
  const allText = messages.map(m => m.content || '').join(' ');
  const context = {};
  
  // Universal patterns - Enhanced for aggressive detection
  const patterns = {
    // Financial - More aggressive patterns with capture groups
    revenue: /\$?([\d,]+(?:\.\d+)?[kmb]?)\s*(?:revenue|arr|annual|yearly|income|sales|turnover|gross|net)/gi,
    arr: /(?:arr|annual recurring revenue|recurring revenue)\s*(?:is|of)?\s*\$?([\d,]+(?:\.\d+)?[kmb]?)|\$?([\d,]+(?:\.\d+)?[kmb]?)\s*(?:arr|annual recurring revenue|recurring revenue)|arr\s+(?:is|of)?\s*(\d+[kmb]?)|my\s+arr\s+(?:is|of)?\s*\$?([\d,]+(?:\.\d+)?[kmb]?)/gi,
    monthly_revenue: /\$?([\d,]+(?:\.\d+)?[kmb]?)\s*(?:monthly|per month|\/mo|mrr|monthly recurring)/gi,
    
    // Time patterns - More comprehensive
    time_spent: /(\d+)\s*hours?\s*(?:per week|weekly|each week|\/week|a week|working|spending)/gi,
    hoursPerWeek: /(\d+)\s*hours?\s*(?:per week|weekly|each week|\/week|a week|working|spending)/gi,
    working_hours: /(?:working|spending|putting in)\s*(\d+)\s*hours?/gi,
    
    // Team size - More aggressive patterns (simplified to single capture group)
    team_size: /(?:team|staff|employees|people|workers|personnel|managing)\s*(?:of\s*)?(\d+)|(\d+)\s*(?:people|employees|team members|staff|workers|personnel|person team|employee)/gi,
    teamSize: /team of (\d+)|(\d+)\s*person team|(\d+)\s*employee|managing.*?(\d+)/gi,
    
    // Overwork indicators
    overworked: /(?:overworked|burnt out|burning out|exhausted|too busy|overwhelmed|stressed|working too much)/gi,
    
    // Business stage indicators
    business_stage: /(?:startup|early stage|growth mode|scaling|exit plan|mature|100k|million|revenue|pre-revenue|post-revenue|bootstrapped|funded|series a|series b|double|triple|expand|grow|scale)/gi,
    growth_stage: /(?:startup|early stage|growth mode|scaling|exit plan|mature|100k|million|revenue|pre-revenue|post-revenue|bootstrapped|funded|series a|series b|double|triple|expand|grow|scale)/gi,
    
    // Goals & Challenges - Enhanced patterns
    goals: /(?:goal|want to|trying to|aim to|objective|target|scale to|grow to|reach|achieve|double|triple|expand|increase)(.{10,50})/gi,
    challenges: /(?:struggle|challenge|problem|stuck|difficult|bottleneck|issue|trouble|pain|frustration|issues with|problems with|struggling with|having trouble|can't|unable to|hard to)(.{10,50})/gi,
    challenge: /(?:challenge|problem|stuck|difficult|bottleneck|issue|trouble|pain|frustration|issues with|problems with|struggling with|having trouble|can't|unable to|hard to)(.{10,50})/gi,
    painPoints: /(?:pain point|bottleneck|struggle|problem|issue|trouble|frustration|stuck|can't scale|overwhelmed|issues with|problems with|struggling with|having trouble|can't|unable to|hard to)(.{10,50})/gi,
    
    // Health (for health coaching)
    current_weight: /(?:weigh|weight).*?(\d{2,3})\s*(?:lbs?|pounds?|kg)/gi,
    goal_weight: /(?:goal|target|want to be|lose to).*?(\d{2,3})\s*(?:lbs?|pounds?|kg)/gi,
    
    // Sales (for sales coaching)  
    quota: /quota.*?\$?[\d,]+(?:\.\d+)?[kmb]?/gi,
    close_rate: /(\d+(?:\.\d+)?%?)\s*(?:close|closing)/gi,
    
    // HVAC specific
    equipment_type: /(?:unit|system|hvac|furnace|ac|heat pump|compressor)(.{5,30})/gi,
    safety_concerns: /(?:safety|danger|hazard|lockout|electrical)(.{5,40})/gi
  };
  
  // Extract all patterns
  Object.entries(patterns).forEach(([key, regex]) => {
    const matches = [...allText.matchAll(regex)];
    if (matches.length > 0) {
      context[key] = matches.map(m => {
        // Find the first non-undefined capture group
        for (let i = 1; i < m.length; i++) {
          if (m[i] !== undefined && m[i] !== null && m[i].trim() !== '') {
            const value = m[i].trim();
            // For team_size, ensure we got a valid number
            if (key === 'team_size' && !/^\d+$/.test(value)) {
              continue; // Skip invalid values
            }
            return value;
          }
        }
        // Fallback to full match if no capture groups (but extract number if present)
        const fullMatch = (m[0] || '').trim();
        if (key === 'team_size') {
          const numMatch = fullMatch.match(/\d+/);
          return numMatch ? numMatch[0] : fullMatch;
        }
        return fullMatch;
      }).filter(v => v && v !== '').slice(0, 2);
      
    }
  });
  
  // Fallback logic for flexible mapping
  if (context.working_hours && !context.hoursPerWeek) {
    context.hoursPerWeek = context.working_hours;
  }
  if (context.monthly_revenue && !context.revenue) {
    context.revenue = context.monthly_revenue;
  }
  // Removed automatic 50+ hours assumption - too aggressive
  // Only infer hours if user explicitly mentions being overworked AND mentions hours
  // if (context.overworked && !context.hoursPerWeek) {
  //   context.hoursPerWeek = ['50+']; // Assume overworked means 50+ hours
  // }
  
  // Fix key mismatches for team size
  if (context.teamSize && !context.team_size) {
    context.team_size = context.teamSize;
  }
  if (context.team_size && !context.teamSize) {
    context.teamSize = context.team_size;
  }
  
  // Fix key mismatches for hours
  if (context.hoursPerWeek && !context.time_spent) {
    context.time_spent = context.hoursPerWeek;
  }
  if (context.time_spent && !context.hoursPerWeek) {
    context.hoursPerWeek = context.time_spent;
  }
  
  // Fix key mismatches for challenges
  if (context.challenge && !context.challenges) {
    context.challenges = context.challenge;
  }
  if (context.challenges && !context.challenge) {
    context.challenge = context.challenges;
  }
  
  // Fix key mismatches for pain points
  if (context.painPoints && !context.pain_points) {
    context.pain_points = context.painPoints;
  }
  if (context.pain_points && !context.painPoints) {
    context.painPoints = context.pain_points;
  }
  
  // Coaching type specific enhancements
  if (coachingType === 'health') {
    // Look for fitness goals, workout preferences, etc
    const fitnessGoals = allText.match(/(?:lose weight|gain muscle|get fit|marathon|strength)/gi);
    if (fitnessGoals) context.fitness_goals = fitnessGoals;
  }
  
  if (coachingType === 'ecommerce') {
    // Look for product, SKU, conversion mentions
    const products = allText.match(/(?:product|sku|item|selling)(.{5,30})/gi);
    if (products) context.products = products.map(p => p.trim()).slice(0, 3);
  }
  
  // Vertical-specific context extraction
  if (vertical === 'hvac') {
    // HVAC specific patterns
    const maintenanceHistory = allText.match(/(?:maintenance|service|repair|breakdown)(.{5,40})/gi);
    if (maintenanceHistory) context.maintenance_history = maintenanceHistory.map(m => m.trim()).slice(0, 2);
    
    const customerFeedback = allText.match(/(?:customer|client|feedback|complaint|review)(.{5,40})/gi);
    if (customerFeedback) context.customer_feedback = customerFeedback.map(m => m.trim()).slice(0, 2);
    
    const responseTime = allText.match(/(?:response|arrive|on site|emergency)(.{5,30})/gi);
    if (responseTime) context.response_time = responseTime.map(m => m.trim()).slice(0, 2);
  }
  
  if (vertical === 'business') {
    // Business specific patterns
    const businessProcesses = allText.match(/(?:process|workflow|system|procedure|sop)(.{5,40})/gi);
    if (businessProcesses) context.business_processes = businessProcesses.map(m => m.trim()).slice(0, 2);
    
    const efficiencyGoals = allText.match(/(?:efficiency|productivity|optimize|streamline|automate)(.{5,40})/gi);
    if (efficiencyGoals) context.efficiency_goals = efficiencyGoals.map(m => m.trim()).slice(0, 2);
  }
  
  if (vertical === 'mentormind') {
    // Mentormind specific patterns (Dan Martell focused) - Enhanced
    const businessStage = allText.match(/(?:startup|early stage|growth mode|scaling|exit plan|mature|100k|million|revenue|pre-revenue|post-revenue|bootstrapped|funded|series a|series b)(.{5,30})/gi);
    if (businessStage) context.business_stage = businessStage.map(m => m.trim()).slice(0, 2);
    
    const painPoints = allText.match(/(?:overwhelmed|too busy|burning out|bottleneck|stuck|can't scale|marketing|retention|acquisition|delegation|hiring|operations|sales|customer service|bookkeeping|admin|management|leadership|team building|process|systems|automation|efficiency|productivity|time management|work life balance|stress|exhaustion|frustration|chaos|disorganized|reactive|firefighting|putting out fires)(.{5,40})/gi);
    if (painPoints) context.pain_points = painPoints.map(m => m.trim()).slice(0, 3);
    
    const delegationNeeds = allText.match(/(?:delegate|hiring|next hire|coo|executive assistant|team|marketing team|operations manager|sales manager|customer service|bookkeeper|admin|virtual assistant|va|contractor|freelancer|employee|staff|team member|help|support|assistance)(.{5,40})/gi);
    if (delegationNeeds) context.delegation_needs = delegationNeeds.map(m => m.trim()).slice(0, 3);
    
    // Extract specific business metrics - Enhanced
    const ltv = allText.match(/(?:ltv|lifetime value|customer lifetime value).*?\$?[\d,]+(?:\.\d+)?[kmb]?/gi);
    if (ltv) context.ltv = ltv.map(m => m.trim()).slice(0, 1);
    
    const cac = allText.match(/(?:cac|customer acquisition|acquisition cost|cost per acquisition).*?\$?[\d,]+(?:\.\d+)?[kmb]?/gi);
    if (cac) context.cac = cac.map(m => m.trim()).slice(0, 1);
    
    const process = allText.match(/(?:process|system|workflow|procedure|sop|standard operating|automation|manual|repetitive|routine|task|operation)(.{5,30})/gi);
    if (process) context.process = process.map(m => m.trim()).slice(0, 3);
    
    // Growth stage indicators
    const growthStage = allText.match(/(?:0-10k|10k-100k|100k-1m|1m-10m|10m\+|pre-revenue|post-revenue|startup|growth|scaling|mature|exit|acquisition|ipo)/gi);
    if (growthStage) context.growth_stage = growthStage.map(m => m.trim()).slice(0, 1);
  }
  
  return context;
};

export const detectMissingData = (context = {}, coachingType = 'business', vertical = 'business') => {
  const missing = [];
  
  // For opsiq vertical, ignore coachingType - it's purely business/employee-focused
  if (vertical === 'opsiq' || coachingType === 'business' || coachingType === 'mentormind' || coachingType === null) {
    if (!context.revenue || !context.revenue.length) missing.push('revenue');
    if (!context.team_size || !context.team_size.length) missing.push('team_size');
    if (!context.time_spent || !context.time_spent.length) missing.push('time_spent');
  }
  
  if (coachingType === 'health') {
    if (!context.current_weight || !context.current_weight.length) missing.push('current_weight');
    if (!context.goal_weight || !context.goal_weight.length) missing.push('goal_weight');
  }
  
  if (vertical === 'hvac') {
    if (!context.equipment_type || !context.equipment_type.length) missing.push('equipment_type');
    if (!context.safety_concerns || !context.safety_concerns.length) missing.push('safety_concerns');
  }
  
  if (vertical === 'mentormind') {
    if (!context.business_stage || !context.business_stage.length) missing.push('business_stage');
    if (!context.pain_points || !context.pain_points.length) missing.push('pain_points');
  }
  
  return missing;
};

export const getSmartQuestion = (missingData = [], coachingType = 'business', vertical = 'business') => {
  if (missingData.length === 0) return null;
  
  const questions = {
    revenue: 'What\'s your current annual revenue?',
    team_size: 'How many people are on your team?',
    time_spent: 'How many hours per week do you spend on operations?',
    current_weight: 'What\'s your current weight?',
    goal_weight: 'What\'s your target weight?',
    equipment_type: 'What type of HVAC equipment do you work with?',
    safety_concerns: 'What safety concerns do you have?',
    business_stage: 'What stage is your business in?',
    pain_points: 'What\'s your biggest challenge right now?'
  };
  
  const nextMissing = missingData[0];
  return questions[nextMissing] || 'Tell me more about your situation.';
};

export const getAnalysisType = (context = {}, coachingType = 'business', vertical = 'business') => {
  // For opsiq vertical, ignore coachingType
  if (vertical === 'opsiq' || coachingType === 'business' || coachingType === 'mentormind') {
    if (context.revenue && context.revenue.length && context.time_spent && context.time_spent.length) return 'roi_analysis';
    if (context.team_size && context.team_size.length && context.time_spent && context.time_spent.length) return 'team_optimization';
    if (context.revenue && context.revenue.length && (!context.team_size || !context.team_size.length)) return 'growth_planning';
  }
  
  if (coachingType === 'health') {
    if (context.current_weight && context.current_weight.length && context.goal_weight && context.goal_weight.length) return 'program_design';
    if (context.current_weight && context.current_weight.length) return 'nutrition_planning';
  }
  
  if (vertical === 'hvac') {
    if (context.equipment_type && context.equipment_type.length && context.safety_concerns && context.safety_concerns.length) return 'safety_optimization';
    if (context.equipment_type && context.equipment_type.length) return 'maintenance_planning';
  }
  
  if (vertical === 'mentormind') {
    if (context.business_stage && context.business_stage.length && context.pain_points && context.pain_points.length) return 'strategic_planning';
    if (context.pain_points && context.pain_points.length) return 'bottleneck_analysis';
  }
  
  return 'general_guidance';
};
